import { z } from "zod/v4";
import type { ResearchObservation } from "./research-acceptance";
import { researchQuotationMatches } from "./research-quotation";

export const ResearchAdjudicationSchema = z.object({
  selectedCandidateId: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  requiresHumanReview: z.boolean(),
  keepAsAdditionalChannel: z.boolean().optional(),
  explanation: z.string().min(1).max(500),
  reviewQuestion: z.string().max(500),
  quotes: z
    .array(
      z.object({
        observationId: z.string(),
        text: z.string().min(5).max(1500),
      }),
    )
    .max(12),
});
export const RESEARCH_ADJUDICATION_JSON_SCHEMA = z.toJSONSchema(
  ResearchAdjudicationSchema,
);
export type ResearchAdjudication = z.infer<typeof ResearchAdjudicationSchema>;

export function adjudicationCanApply(
  decision: ResearchAdjudication,
  verifiedCandidateIds: string[],
  observations: ResearchObservation[],
): boolean {
  return (
    !decision.requiresHumanReview &&
    decision.confidence >= 0.7 &&
    decision.selectedCandidateId != null &&
    verifiedCandidateIds.includes(decision.selectedCandidateId) &&
    decision.quotes.length > 0 &&
    decision.quotes.every((quote) =>
      observations.some(
        (observation) =>
          observation.id === quote.observationId &&
          researchQuotationMatches(observation.text, quote.text),
      ),
    )
  );
}

/** Diagnostics are not a request for a broker decision. */
export function researchRequiresHumanReview(
  evidence: unknown,
): boolean | undefined {
  if (!evidence || typeof evidence !== "object") return undefined;
  const value = (evidence as { requiresHumanReview?: unknown })
    .requiresHumanReview;
  return typeof value === "boolean" ? value : undefined;
}
